import { adminApiAxios, apiAxios } from '@/api'
import { keyBy } from 'lodash'

import {
  receiveSingleTransactionRule,
  receiveTransactionRules,
  TransactionRule,
  TransactionRuleCategoryType,
  TransactionRuleType,
  TransactionRuleUse,
} from '../../reducers/admin/transactionRulesReducer'
import { ArchiveReason } from '@/constants/transactionRuleConstants'
import { fetchIfNeededWrapper, fetchWrapper } from '../../reducers/fetch'
import { OrderBy } from '@/v2-features/admin/paginated-table/AdminBaseTable'
import {
  CursorPaginatedResponse,
  PrismaCursorPagination,
} from '@/utils/fetchHelpers'
import { serializeOrderBy } from '@/utils/sortHelpers'
import { TransactionRuleFilter } from '@/features/admin/autocategorization/useTransactionRuleFilters'

export const FETCH_ALL_TRANSACTION_RULES_KEY = 'FETCH_ALL_TRANSACTION_RULES_KEY'

export const fetchAllTransactionRulesIfNeeded = () =>
  fetchIfNeededWrapper({
    defaultErrorMessage: 'There was an error fetching all transaction rule.',
    fetchKey: FETCH_ALL_TRANSACTION_RULES_KEY,
    fetchFunction: (dispatch) =>
      adminApiAxios
        .get<TransactionRule[]>('/finances/api/v1/admin/transaction_rules')
        .then((json) => {
          dispatch(receiveTransactionRules(keyBy(json.data, 'id')))
          return json.data
        }),
  })

export const fetchPaginatedTransactionRules = async ({
  filter = {},
  pagination = { take: 100, cursor: undefined },
  orderBy = { column: 'ruleText', direction: 'ascending' },
  select = [],
}: {
  filter?: TransactionRuleFilter
  pagination?: PrismaCursorPagination
  orderBy?: OrderBy
  select?: string[]
}) => {
  const res = await adminApiAxios.get<CursorPaginatedResponse<TransactionRule>>(
    '/finances/api/v2/admin/transaction_rules',
    {
      params: {
        filter,
        take: pagination.take,
        cursor: pagination.cursor,
        orderBy: serializeOrderBy(orderBy),
        selectRelation: select,
      },
      paramsSerializer: {
        indexes: false,
      },
    }
  )
  return res.data
}

export const CREATE_TRANSACTION_RULE_KEY = 'CREATE_TRANSACTION_RULE_KEY'
export const createTransactionRule = (data: {
  userId: number | undefined
  ruleUse: TransactionRuleUse
  ruleText: string
  ruleType: TransactionRuleType
  amountInCents: string | undefined
  transactionCategoryType: TransactionRuleCategoryType | undefined | null
  transactionCategoryId: number | undefined | null
}) =>
  fetchWrapper({
    fetchKey: CREATE_TRANSACTION_RULE_KEY,
    defaultErrorMessage: 'There was an error creating transaction rule.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<TransactionRule>('/finances/api/v1/admin/transaction_rules', data)
        .then((json) => {
          dispatch(receiveSingleTransactionRule(json.data))
          return json.data
        }),
  })

export const ARCHIVE_TRANSACTION_RULE_KEY = (id: number) =>
  `ARCHIVE_TRANSACTION_RULE_KEY${id}`
export const archiveTransactionRule = (
  id: number,
  body: { reason: ArchiveReason; description?: string }
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error archiving the transaction rule.',
    defaultValue: false as const,
    fetchKey: ARCHIVE_TRANSACTION_RULE_KEY(id),
    fetchFunction: (dispatch) =>
      adminApiAxios
        .delete<TransactionRule>(
          `/finances/api/v1/admin/transaction_rules/${id}`,
          { data: body }
        )
        .then((json) => {
          dispatch(receiveSingleTransactionRule(json.data))
          return json.data
        }),
  })

type RestoreTransactionRuleResponse = {
  restored: TransactionRule
  archived: TransactionRule
}

export const RESTORE_TRANSACTION_RULE_KEY = (id: number) =>
  `RESTORE_TRANSACTION_RULE_KEY${id}`
export const restoreTransactionRule = (id: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error restoring the transaction rule.',
    defaultValue: false as const,
    fetchKey: RESTORE_TRANSACTION_RULE_KEY(id),
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<RestoreTransactionRuleResponse>(
          `/finances/api/v2/admin/transaction_rules/${id}/restore`
        )
        .then((json) => {
          dispatch(receiveSingleTransactionRule(json.data.restored))
          dispatch(receiveSingleTransactionRule(json.data.archived))
          return json.data
        }),
  })

export const fetchSingleTransactionRule = (id: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an receiving.',
    fetchFunction: (dispatch) =>
      apiAxios.get(`/finances/api/v1/transaction_rules/${id}`).then((json) => {
        dispatch(receiveSingleTransactionRule(json.data))
        return json.data
      }),
  })
